/**
 * 记录 axios 最后一次失败，供 DBG 浮窗展示（与 CRM 移动端 apiLastError 对齐）。
 */

export type ApiErrorCategory =
  | "network_timeout"
  | "network_unreachable"
  | "http_401"
  | "http_403"
  | "http_404"
  | "http_429"
  | "http_5xx"
  | "http_4xx_other"
  | "unknown";

export type ApiLastErrorSnapshot = {
  at: string;
  category: ApiErrorCategory;
  categoryLabel: string;
  hint: string;
  method: string;
  requestUrl: string;
  status?: number;
  statusText?: string;
  serverMessage?: string;
  axiosCode?: string;
  axiosMessage?: string;
};

let snapshot: ApiLastErrorSnapshot | null = null;
const listeners = new Set<() => void>();

export function subscribeApiLastError(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getApiLastErrorSnapshot(): ApiLastErrorSnapshot | null {
  return snapshot;
}

function notify(): void {
  listeners.forEach((fn) => {
    fn();
  });
}

function joinRequestUrl(baseURL: string | undefined, url: string | undefined): string {
  const b = (baseURL || "").replace(/\/$/, "");
  const u = url || "";
  if (!u) return b || "(unknown path)";
  if (u.startsWith("http")) return u;
  const pathPart = u.startsWith("/") ? u : `/${u}`;
  return `${b}${pathPart}`;
}

function buildSnapshot(error: unknown): ApiLastErrorSnapshot {
  const ax = error as {
    response?: {
      status?: number;
      statusText?: string;
      data?: { message?: string; success?: boolean };
    };
    config?: { method?: string; url?: string; baseURL?: string };
    code?: string;
    message?: string;
  };

  const method = String(ax.config?.method || "GET").toUpperCase();
  const requestUrl = joinRequestUrl(ax.config?.baseURL, ax.config?.url);
  const res = ax.response;
  const at = new Date().toISOString();

  const serverMsg =
    res?.data &&
    typeof res.data === "object" &&
    res.data !== null &&
    "message" in res.data &&
    typeof (res.data as { message?: unknown }).message === "string"
      ? (res.data as { message: string }).message
      : undefined;

  if (!res) {
    const code = ax.code;
    const msg = ax.message || "";

    if (code === "ECONNABORTED" || /timeout/i.test(msg)) {
      return {
        at,
        category: "network_timeout",
        categoryLabel: "请求超时",
        hint: "约定时间内未收到响应。",
        method,
        requestUrl,
        axiosCode: code,
        axiosMessage: msg,
      };
    }

    if (
      code === "ERR_NETWORK" ||
      code === "ENOTFOUND" ||
      /Network Error/i.test(msg)
    ) {
      return {
        at,
        category: "network_unreachable",
        categoryLabel: "网络不可达 / 可能被 CORS 拦截",
        hint: "未收到 HTTP 状态码。",
        method,
        requestUrl,
        axiosCode: code,
        axiosMessage: msg,
      };
    }

    return {
      at,
      category: "unknown",
      categoryLabel: "未知错误（无响应体）",
      hint: "请结合 Network 查看。",
      method,
      requestUrl,
      axiosCode: code,
      axiosMessage: msg,
    };
  }

  const status = res.status;
  const statusText = res.statusText;

  if (status === 401) {
    return {
      at,
      category: "http_401",
      categoryLabel: "401 鉴权未通过",
      hint: "令牌未接受或已过期。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  if (status === 403) {
    return {
      at,
      category: "http_403",
      categoryLabel: "403 无权限",
      hint: "已通过鉴权但无资源权限。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  if (status === 404) {
    return {
      at,
      category: "http_404",
      categoryLabel: "404",
      hint: "路径或方法不匹配。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  if (status === 429) {
    return {
      at,
      category: "http_429",
      categoryLabel: "429 限流",
      hint: "请求过频。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  if (status >= 500) {
    return {
      at,
      category: "http_5xx",
      categoryLabel: `5xx (${status})`,
      hint: "服务端异常。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  if (status >= 400) {
    return {
      at,
      category: "http_4xx_other",
      categoryLabel: `4xx (${status})`,
      hint: "见服务端 message。",
      method,
      requestUrl,
      status,
      statusText,
      serverMessage: serverMsg,
    };
  }

  return {
    at,
    category: "unknown",
    categoryLabel: `HTTP ${status}`,
    hint: "非常见状态码。",
    method,
    requestUrl,
    status,
    statusText,
    serverMessage: serverMsg,
  };
}

export function recordApiErrorFromAxios(error: unknown): void {
  try {
    snapshot = buildSnapshot(error);
    notify();
  } catch {
    snapshot = {
      at: new Date().toISOString(),
      category: "unknown",
      categoryLabel: "记录失败",
      hint: "无法解析错误对象",
      method: "?",
      requestUrl: "?",
    };
    notify();
  }
}
